'use client';

import Image from 'next/image';
import type { ReactNode } from 'react';

import { cn } from '@kit/ui/utils';

import type {
  ChannelNode,
  FlowLayout,
  ProfileNode,
  Tone,
} from './types';

// ---------- ChannelPill ----------

export function ChannelPill(props: {
  icon?: React.ComponentType<{ className?: string }>;
  customIcon?: ReactNode;
  label: string;
  volume?: string;
}) {
  const { icon: Icon, customIcon, label, volume } = props;
  return (
    <div className="flex w-full max-w-[140px] flex-col items-center gap-1.5">
      <div className="border-border bg-background flex h-10 w-10 items-center justify-center rounded-full border">
        {customIcon ? (
          <span className="text-foreground flex h-5 w-5 items-center justify-center">
            {customIcon}
          </span>
        ) : Icon ? (
          <Icon className="text-muted-foreground h-4 w-4" />
        ) : null}
      </div>
      <span className="text-muted-foreground text-center text-[11px] leading-tight font-medium">
        {label}
      </span>
      {volume ? (
        <span className="text-foreground/80 text-center text-[10px] leading-tight font-semibold tabular-nums">
          {volume}
        </span>
      ) : null}
    </div>
  );
}

// ---------- DestinationPanel ----------
// Generic destination panel. For the aXcelerate marketing page we pass the
// aXcelerate logo + "Student Management System" subtitle via the two
// optional render props; elsewhere (admin, docs) a consumer can pass their
// own image/subtitle or just a label string.

export function DestinationPanel({
  lightImageSrc,
  darkImageSrc,
  imageWidth = 147,
  imageHeight = 24,
  imageHeightClass = 'h-5',
  subtitle,
  fallbackLabel,
}: {
  lightImageSrc?: string;
  darkImageSrc?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageHeightClass?: string;
  subtitle?: string;
  fallbackLabel?: string;
}) {
  return (
    <div className="border-border bg-muted/40 flex w-full flex-col items-center gap-1 rounded-2xl border px-4 py-3">
      {darkImageSrc && (
        <Image
          src={darkImageSrc}
          alt={fallbackLabel ?? ''}
          width={imageWidth}
          height={imageHeight}
          className={cn('hidden w-auto dark:block', imageHeightClass)}
        />
      )}
      {lightImageSrc && (
        <Image
          src={lightImageSrc}
          alt={fallbackLabel ?? ''}
          width={imageWidth}
          height={imageHeight}
          className={cn(
            'w-auto',
            darkImageSrc ? 'dark:hidden' : '',
            imageHeightClass,
          )}
        />
      )}
      {!lightImageSrc && !darkImageSrc && fallbackLabel && (
        <span className="text-sm font-semibold">{fallbackLabel}</span>
      )}
      {subtitle && (
        <span className="text-muted-foreground text-[10px] font-medium tracking-wider uppercase">
          {subtitle}
        </span>
      )}
    </div>
  );
}

// ---------- ProfileCard ----------

export function ProfileCard({
  node,
  colSpan,
}: {
  node: ProfileNode;
  colSpan: number;
}) {
  const isLeagen = node.tone === 'leagen';
  const isSystem = node.tone === 'system';
  const Icon = node.icon;

  // Single-lane cards cap at max-w-[140px] so there's always a
  // generous gutter between them for edges/labels. Multi-lane cards
  // (colSpan > 1) intentionally stretch to fill their span — the
  // stretch IS the signal to the viewer that this card covers
  // multiple input channels (e.g. a receptionist that handles both
  // email and phone).
  const widthClass = colSpan > 1 ? 'w-full' : 'w-full max-w-[140px]';

  return (
    <div
      className={cn(
        'relative flex h-full flex-col items-center rounded-xl border px-2.5 py-2.5 text-center',
        widthClass,
        isSystem
          ? 'border-muted-foreground/30 bg-muted/30 border-dashed'
          : 'border-border bg-card',
      )}
    >
      <div className="relative">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full',
            isLeagen
              ? 'bg-primary/10'
              : isSystem
                ? 'bg-background'
                : 'bg-muted',
          )}
        >
          {node.customIcon ? (
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center',
                isLeagen ? 'text-primary' : 'text-foreground',
              )}
            >
              {node.customIcon}
            </span>
          ) : Icon ? (
            <Icon
              className={cn(
                'h-4 w-4',
                isLeagen
                  ? 'text-primary'
                  : isSystem
                    ? 'text-muted-foreground'
                    : 'text-foreground',
              )}
            />
          ) : null}
        </div>
        {node.cost && (
          <span
            className={cn(
              'border-background absolute -top-1 -right-2 rounded-full border-2 px-1 py-0.5 text-[9px] leading-none font-bold',
              isLeagen
                ? 'bg-primary text-primary-foreground'
                : 'bg-foreground text-background',
            )}
            title={node.costSub ? `${node.cost}${node.costSub}` : node.cost}
          >
            {node.cost}
          </span>
        )}
      </div>

      <p className="mt-1.5 text-[11px] leading-tight font-semibold">
        {node.count !== undefined && node.count > 1 && (
          <span className="text-sky-300 tabular-nums">{node.count}× </span>
        )}
        {node.title}
      </p>
      {node.subtitle && (
        <p className="text-muted-foreground mt-0.5 text-[10px] leading-snug">
          {node.subtitle}
        </p>
      )}
      {node.status && (
        <div className="mt-1 flex items-center gap-1 text-[9px]">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              node.status.tone === 'green' ? 'bg-emerald-500' : 'bg-amber-500',
            )}
          />
          <span className="text-muted-foreground">{node.status.label}</span>
        </div>
      )}
      {node.meta && (
        <p className="text-muted-foreground/70 mt-0.5 text-[9px] italic">
          {node.meta}
        </p>
      )}
    </div>
  );
}

// ---------- FlowList (mobile / Normal mode fallback) ----------
// Renders the profile nodes from a layout as a simple vertical list.
// Used on mobile and when the calling page switches to Normal mode.

export function FlowList({
  layout,
  tone,
  className,
}: {
  layout: FlowLayout;
  tone: Tone;
  className?: string;
}) {
  const items = layout.nodes.filter(
    (n): n is ProfileNode => n.kind === 'profile',
  );

  // Rendered as a 3-column profile-card grid so the normal (non-
  // detailed) view reads as a roster of the RTO's actual handlers
  // rather than a dense row list. Each card has a large centered
  // icon, title, and subtitle — matching the visual identity of a
  // "person card" that RTO operators recognise from CRM tools.
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {items.map((node) => (
        <div
          key={node.id}
          // min-h locks every card to the same height regardless of
          // which grid row it sits on and whether its content has a
          // subtitle, status pill, or only a title. Picked 220px so
          // the tallest variant (title + 2-line subtitle + status pill)
          // fits comfortably with a few pixels of breathing room. All
          // other cards flex-justify their content top-aligned inside
          // the same box, so every row of cards — Traditional and
          // Leagen, row 1 and row 2+ — lines up pixel-for-pixel.
          className={cn(
            'relative flex min-h-[220px] flex-col items-center gap-3 rounded-2xl border px-4 py-5 text-center',
            tone === 'leagen'
              ? 'border-primary/20 bg-primary/5'
              : 'border-border bg-card',
          )}
        >
          {node.cost && (
            <span className="border-background bg-foreground text-background absolute top-3 right-3 rounded-full border px-2 py-0.5 text-[10px] leading-none font-bold">
              {node.cost}
            </span>
          )}
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full',
              tone === 'leagen' ? 'bg-primary/10' : 'bg-muted',
            )}
          >
            {node.customIcon ? (
              <span className="flex h-7 w-7 items-center justify-center">
                {node.customIcon}
              </span>
            ) : node.icon ? (
              <node.icon className="text-muted-foreground h-6 w-6" />
            ) : null}
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold leading-tight">
              {node.count !== undefined && node.count > 1 && (
                <span className="text-sky-300 tabular-nums">
                  {node.count}×{' '}
                </span>
              )}
              {node.title}
            </p>
            {node.subtitle && (
              <p className="text-muted-foreground text-[11px] leading-snug">
                {node.subtitle}
              </p>
            )}
            {node.status && (
              <div className="mt-2 flex items-center justify-center gap-1.5">
                <span
                  aria-hidden
                  className={cn(
                    'inline-block h-1.5 w-1.5 rounded-full',
                    node.status.tone === 'green'
                      ? 'bg-emerald-500'
                      : 'bg-amber-500',
                  )}
                />
                <span
                  className={cn(
                    'text-[11px] font-medium',
                    node.status.tone === 'green'
                      ? 'text-emerald-500'
                      : 'text-amber-500',
                  )}
                >
                  {node.status.label}
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Re-export the node-row types so consumers can pass them around easily.
export type { ChannelNode, ProfileNode };
